import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import type { Bridge } from '@casa/bridge';
import { startDriverHost, type DriverCatalog, type DriverHostLink } from '@casa/driver-host';
import type { Reading, SourceDescription } from '@casa/driver-sdk';
import { refreshIntervalS } from '@casa/drivers/config';
import { tilesOf, withoutPlaceholders, type Dashboard, type Screen, type Tile } from '@casa/model';
import { createClockCheck, createRuntime, type ClockCheck, type DriverLink, type RuntimeSource, type SourceState, type SourceStatus } from '@casa/runtime';
import { createDriverWorker } from '../drivers/drivers.ts';
import { collectSources, type SourceRef, type TileSources } from './data_sources.ts';

// The app's binding of `@casa/runtime`: one driver per connection of the dashboard (each in its
// own sandboxed Worker, behind the permission gate), the sources of every tile scheduled by the
// runtime, and React hooks for the tiles.

/**
 * How a connection is doing now (C-1), from its sources' states: working when one of them reads,
 * failing (with the reason) when all that tried failed, waiting before any answer, unused without tiles.
 */
export type ConnectionStatus =
    | { status: 'ok' }
    | { status: 'failing'; reason: string }
    | { status: 'waiting' }
    | { status: 'unused' };

export interface LiveData {
    sourcesOf(tile: Tile) : TileSources | undefined;
    state(ref: SourceRef) : SourceState;
    subscribe(ref: SourceRef, listener: () => void) : () => void;
    /** Only the shown screen's sources are refreshed. */
    showScreen(screen: Screen) : void;
    setForeground(foreground: boolean) : void;
    connectionStatus(name: string) : ConnectionStatus;
    /** Calls the listener when a source of the connection changes, so its status may have. */
    subscribeConnection(name: string, listener: () => void) : () => void;
    /** Sends a command to the source's driver; see `Runtime.command`. */
    command(ref: SourceRef, name: string, arg?: unknown, optimistic?: Reading[] | null) : Promise<void>;
    /**
     * What the source gives and the commands it takes, as its driver describes them (M-3), asked once
     * per source; a failed answer is asked again the next time.
     */
    describe(ref: SourceRef) : Promise<SourceDescription>;
    readonly clock: ClockCheck;
    dispose() : void;
}

const keyOf = (ref: SourceRef) : string => `${ref.connection}\u0000${ref.source.id}`;

/** A link for a connection whose driver does not run: its sources fail with the reason. */
function missingDriver(reason: string, events: { failed(sourceId: string, reason: string) : void }) : DriverLink {
    return {
        async refresh(sourceIds) {
            for (const sourceId of sourceIds) {
                events.failed(sourceId, reason);
            }
        },
        command: () => Promise.reject(new Error(reason)),
        dispose() {},
    };
}

export function startLiveData(dashboard: Dashboard, bridge: Bridge, drivers: DriverCatalog) : LiveData {
    const { tiles, byConnection } = collectSources(dashboard);
    const clock = createClockCheck();

    const sources: RuntimeSource[] = [];
    for (const [name, list] of byConnection) {
        const connection = dashboard.connections[name];
        for (const source of list) {
            const own = typeof source.refreshInterval === 'number' ? source.refreshInterval : undefined;
            sources.push({
                key: keyOf({ connection: name, source }),
                connection: name,
                sourceId: source.id,
                intervalS: connection ? refreshIntervalS(connection.driver, connection.config, own) : (own ?? 30),
            });
        }
    }

    // The drivers' own links, for describing their sources (`describe`); the runtime has its own of them.
    const hosts = new Map<string, DriverHostLink>();
    const descriptions = new Map<string, Promise<SourceDescription>>();

    const runtime = createRuntime({
        sources,
        connect(name, events) {
            const connection = dashboard.connections[name];
            if (!connection) {
                return missingDriver(`no connection named "${name}"`, events);
            }

            const driver = drivers.resolve(connection.driver);
            if (!driver.ok) {
                return missingDriver(driver.reason, events);
            }

            const host = startDriverHost({
                bridge,
                createWorker: createDriverWorker,
                connection: name,
                driver: driver.code,
                permissions: driver.permissions,
                // A credential kept apart that nothing resolved reaches the driver as missing (secrets SE-2).
                config: withoutPlaceholders(connection.config),
                sources: byConnection.get(name) ?? [],
                events,
                log: (message) => console.warn(message),
                onServerDate: (date, receivedAt) => clock.observe(date, receivedAt),
            });
            hosts.set(name, host);

            return host;
        },
    });

    return {
        sourcesOf: (tile) => tiles.get(tile.id),
        state: (ref) => runtime.state(keyOf(ref)),
        subscribe: (ref, listener) => runtime.subscribe(keyOf(ref), listener),
        showScreen(screen) {
            runtime.setActive(tilesOf(screen).flatMap((tile) => tiles.get(tile.id)?.all.map(keyOf) ?? []));
        },
        setForeground: (foreground) => runtime.setForeground(foreground),
        command: (ref, name, arg, optimistic) => runtime.command(keyOf(ref), name, arg, optimistic),
        describe(ref) {
            const key = keyOf(ref);
            const known = descriptions.get(key);
            if (known) {
                return known;
            }

            const host = hosts.get(ref.connection);
            if (!host) {
                return Promise.reject(new Error(`no driver runs for "${ref.connection}"`));
            }

            // The source's options as the driver takes them: without the id and interval the dashboard gives it.
            const { id: _id, refreshInterval: _every, ...options } = ref.source;
            const asked = host.ask({ kind: 'describe', options });
            descriptions.set(key, asked);
            asked.catch(() => descriptions.delete(key));

            return asked;
        },
        connectionStatus(name) {
            const states = (byConnection.get(name) ?? []).map((source) => runtime.state(keyOf({ connection: name, source })));
            if (states.length === 0) {
                return { status: 'unused' };
            }
            if (states.some((state) => state.status === 'ok' || state.status === 'stale')) {
                return { status: 'ok' };
            }

            const failing = states.find((state) => state.status === 'failing');

            return failing ? { status: 'failing', reason: failing.failure ?? 'it failed' } : { status: 'waiting' };
        },
        subscribeConnection(name, listener) {
            const unsubscribes = (byConnection.get(name) ?? []).map((source) => runtime.subscribe(keyOf({ connection: name, source }), listener));

            return () => {
                for (const unsubscribe of unsubscribes) {
                    unsubscribe();
                }
            };
        },
        clock,
        dispose: () => runtime.dispose(),
    };
}

export const LiveDataContext = createContext<LiveData | null>(null);
const NO_SUBSCRIPTION = () => () => {};

/** The latest readings of a source; undefined until the driver has reported, or without a source. */
export function useReadings(ref: SourceRef | undefined) : Reading[] | null | undefined {
    const live = useContext(LiveDataContext);

    return useSyncExternalStore(
        live && ref ? (listener) => live.subscribe(ref, listener) : NO_SUBSCRIPTION,
        () => (live && ref ? live.state(ref).readings : undefined),
    );
}

/** `useReadings` for several sources; the list of sources must keep its length. */
export function useReadingsList(refs: readonly (SourceRef | undefined)[]) : (Reading[] | null | undefined)[] {
    const live = useContext(LiveDataContext);
    const [, setVersion] = useState(0);

    useEffect(() => {
        if (!live) {
            return;
        }

        const unsubscribes = refs.flatMap((ref) => (ref ? [live.subscribe(ref, () => setVersion((version) => version + 1))] : []));

        return () => {
            for (const unsubscribe of unsubscribes) {
                unsubscribe();
            }
        };
        // The refs come from `collectSources` and keep their identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [live, ...refs]);

    return refs.map((ref) => (live && ref ? live.state(ref).readings : undefined));
}

export function useTileSources(tile: Tile) : TileSources | undefined {
    return useContext(LiveDataContext)?.sourcesOf(tile);
}

const SEVERITY: Readonly<Record<SourceStatus, number>> = { ok: 0, waiting: 1, stale: 2, failing: 3 };

export interface TileStatus {
    /** The worst status of the tile's sources. */
    status: SourceStatus;
    /** Why sources fail, without repeats. */
    failures: string[];
}

/** The status of a tile: the worst of its sources' (a tile without sources is `ok`). */
export function useTileStatus(tile: Tile) : TileStatus {
    const live = useContext(LiveDataContext);
    const refs = live?.sourcesOf(tile)?.all ?? [];
    const [, setVersion] = useState(0);

    useEffect(() => {
        if (!live) {
            return;
        }

        const unsubscribes = refs.map((ref) => live.subscribe(ref, () => setVersion((version) => version + 1)));

        return () => {
            for (const unsubscribe of unsubscribes) {
                unsubscribe();
            }
        };
        // The refs come from `collectSources` and keep their identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [live, ...refs]);

    let status: SourceStatus = 'ok';
    const failures = new Set<string>();
    for (const ref of refs) {
        const state = live?.state(ref);
        if (!state) {
            continue;
        }
        if (SEVERITY[state.status] > SEVERITY[status]) {
            status = state.status;
        }
        if (state.failure) {
            failures.add(`${ref.connection}: ${state.failure}`);
        }
    }

    return { status, failures: [...failures] };
}